// batch-68 gespreksvoorbereiding (FO-346 §3.7) - ontwikkeldoelen en doorgroeiwens
// van een medewerker. Vastgelegd in het dossier (tab Ontwikkeling) of vanuit het
// verslagformulier van een gesprek (bronGesprekId). Niet-interne doelen ziet de
// medewerker zelf in de portal (Mijn opleidingen > Mijn ontwikkeling).

import { z } from 'zod'

export const ONTWIKKEL_DOEL_SOORTEN = ['DOEL', 'DOORGROEIWENS'] as const
export type OntwikkelDoelSoort = (typeof ONTWIKKEL_DOEL_SOORTEN)[number]

export const ONTWIKKEL_DOEL_SOORT_LABELS: Record<OntwikkelDoelSoort, string> = {
  DOEL: 'Ontwikkeldoel',
  DOORGROEIWENS: 'Doorgroeiwens',
}

export const ONTWIKKEL_DOEL_STATUSSEN = ['OPEN', 'BEZIG', 'BEHAALD', 'VERVALLEN'] as const
export type OntwikkelDoelStatus = (typeof ONTWIKKEL_DOEL_STATUSSEN)[number]

export const ONTWIKKEL_DOEL_STATUS_LABELS: Record<OntwikkelDoelStatus, string> = {
  OPEN: 'Open',
  BEZIG: 'Bezig',
  BEHAALD: 'Behaald',
  VERVALLEN: 'Vervallen',
}

const YMD = /^\d{4}-\d{2}-\d{2}$/

/** Nieuw doel of nieuwe doorgroeiwens. */
export const ontwikkelDoelCreateSchema = z.object({
  soort: z.enum(ONTWIKKEL_DOEL_SOORTEN).default('DOEL'),
  omschrijving: z
    .string()
    .trim()
    .min(1, 'Beschrijf het doel of de wens')
    .max(2000, 'Maximaal 2000 tekens'),
  streefdatum: z.string().regex(YMD, 'Kies een geldige datum').nullable().optional(),
  status: z.enum(ONTWIKKEL_DOEL_STATUSSEN).optional(),
  bronGesprekId: z.string().trim().min(1).max(64).nullable().optional(),
  intern: z.boolean().optional(),
})
export type OntwikkelDoelCreateInput = z.infer<typeof ontwikkelDoelCreateSchema>

/**
 * Wijzigen. Accepteert de eigen read-DTO terug (nullable streefdatum en
 * bron-gesprek zijn legitieme leeg-waarden); onbekende velden negeert zod.
 */
export const ontwikkelDoelUpdateSchema = z.object({
  soort: z.enum(ONTWIKKEL_DOEL_SOORTEN).optional(),
  omschrijving: z
    .string()
    .trim()
    .min(1, 'Beschrijf het doel of de wens')
    .max(2000, 'Maximaal 2000 tekens')
    .optional(),
  streefdatum: z.string().regex(YMD, 'Kies een geldige datum').nullable().optional(),
  status: z.enum(ONTWIKKEL_DOEL_STATUSSEN).optional(),
  bronGesprekId: z.string().trim().min(1).max(64).nullable().optional(),
  intern: z.boolean().optional(),
})
export type OntwikkelDoelUpdateInput = z.infer<typeof ontwikkelDoelUpdateSchema>

export interface OntwikkelDoelDTO {
  id: string
  employeeId: string
  soort: OntwikkelDoelSoort
  omschrijving: string
  /** NL-kalenderdag yyyy-mm-dd of null. */
  streefdatum: string | null
  status: OntwikkelDoelStatus
  bronGesprekId: string | null
  /** Titel van het bron-gesprek (neutraal bij een verzuimgesprek zonder recht). */
  bronGesprekTitel: string | null
  eigenaarNaam: string | null
  intern: boolean
  createdAt: string
  updatedAt: string
}

/** Portal: de eigen, niet-interne doelen (geen eigenaar-id's of interne velden). */
export interface MijnOntwikkelDoelDTO {
  id: string
  soort: OntwikkelDoelSoort
  omschrijving: string
  streefdatum: string | null
  status: OntwikkelDoelStatus
  vastgelegdOp: string
}

export interface MijnOntwikkelingDTO {
  doelen: MijnOntwikkelDoelDTO[]
  /** De laatst vastgelegde doorgroeiwens (of null). */
  doorgroeiwens: MijnOntwikkelDoelDTO | null
}
